import { FC, useMemo, useRef, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from "recharts";
import {
  SquareUserRound,
  TrendingDown,
  TrendingUp,
  UserRoundPlus,
} from "lucide-react";
import LoadingSpinner from "src/components/reusable/LoadingSpinner";
import { DashboardExportButton, DashboardSectionHeader, DashboardNarrativePanel, DefinitionTooltip } from "src/components/common";
import { useDashboardNarrative } from "src/hooks/useDashboardNarrative";
import {
  useFacebookFollowersReport,
  type FollowersTrendGranularity,
} from "../../hooks/useOrganicMediaData";
import OrganicDateRangePicker from "./OrganicDateRangePicker";
import { getFacebookFollowersDefinitionInfo } from "./organicMediaDefinitions";
import { normalizeApiLang } from "src/utils/apiLang";
import { CHART_PRIMARY_COLOR } from "src/components/charts/chartPalette";
import { mirrorChartMargin, rtlChartProps } from "src/components/charts/chartRtl";
import { formatNumber, isArabicLanguage, toArabicIndicDigitsInString } from "src/utils/numberFormat";

interface FacebookFollowersReportProps {
  dateFilters: import("./FacebookContentReport").OrganicDateFilters;
  topCountriesCount: number | undefined;
  topCitiesCount: number | undefined;
  /* eslint-disable-next-line no-unused-vars */
  setTopCountriesCount: (value: number | undefined) => void;
  /* eslint-disable-next-line no-unused-vars */
  setTopCitiesCount: (value: number | undefined) => void;
  todayISO: string;
}

const MAX_TOP = 10;

const GEO_BAR_COLORS = [
  "#5ca6f8",
  "#31cdbc",
  "#a855f7",
  "#5ca6f8",
  "#16b981",
  "#f59e0b",
];

const parseTrendDateMs = (value: string) => {
  const isoMatch = String(value ?? "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return Date.UTC(Number(isoMatch[1]), Number(isoMatch[2]) - 1, Number(isoMatch[3]));
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? Number.NaN : parsed.getTime();
};

const formatTrendAxisDate = (value: string | number) => {
  const raw = String(value ?? "");
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  }

  const monthMatch = raw.match(/^(\d{4})-(\d{2})$/);
  if (monthMatch) {
    return `${monthMatch[2]}-${monthMatch[1]}`;
  }

  return raw;
};

const formatComparisonLabel = (label: string | undefined, t: (key: string) => string) => {
  const trimmed = (label ?? "").trim();
  const normalized = trimmed.toLowerCase();
  if (
    !normalized ||
    normalized === "vs last period" ||
    normalized === "vs previous period" ||
    trimmed === "مقارنة بالفترة السابقة"
  ) {
    return t("organicMedia.tooltips.vsPreviousPeriod").replace(/:$/, "");
  }
  return label as string;
};

const formatKpiCardValue = (title: string, value: string, language: string) => {
  const normalizedTitle = title.trim().toLowerCase();
  if (normalizedTitle.includes("avg") && normalizedTitle.includes("new followers")) {
    const numeric = Number(value.replace(/,/g, ""));
    if (Number.isFinite(numeric)) {
      return formatNumber(Math.round(numeric), {}, language);
    }
  }
  return value;
};

function FollowersTrendTooltip({ active, label, payload }: TooltipProps<number, string>) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload as { valueFormattedValue?: string; dateFormattedValue?: string } | undefined;
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");

  return (
    <div className="organic-tooltip organic-tooltip--kpi">
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">
            {t("organicMedia.tooltips.date")}
          </span>
          <strong className="organic-tooltip__meta-value">
            {point?.dateFormattedValue ??
              (isRTL
                ? toArabicIndicDigitsInString(formatTrendAxisDate(String(label ?? "")))
                : formatTrendAxisDate(String(label ?? "")))}
          </strong>
        </div>
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.newFollowers")}</span>
          <strong className="organic-tooltip__meta-value">
            {point?.valueFormattedValue ?? formatNumber(Number(payload[0]?.value ?? 0), {}, i18n.language)}
          </strong>
        </div>
      </div>
    </div>
  );
}

interface FollowersGeoRow {
  name: string;
  followers: number;
  followersFormattedValue?: string;
  change_percent?: number;
}

interface RankedHoverState {
  clientX: number;
  clientY: number;
  row: FollowersGeoRow;
}

// Keeps the cursor-following tooltip inside the viewport — without clamping,
// rows near the right/bottom edge (e.g. the "Top Cities" panel) push it off-screen.
const CURSOR_TOOLTIP_OFFSET = 14;
const CURSOR_TOOLTIP_WIDTH = 260;
const CURSOR_TOOLTIP_HEIGHT = 110;
const VIEWPORT_MARGIN = 8;

const getCursorTooltipPosition = (clientX: number, clientY: number) => {
  const viewportWidth = typeof window === "undefined" ? 1024 : window.innerWidth;
  const viewportHeight = typeof window === "undefined" ? 768 : window.innerHeight;

  let viewportLeftEdge = 0;
  let viewportRightEdge = viewportWidth;
  const sidebarRect = typeof document === "undefined" ? undefined : document.querySelector(".sidebar")?.getBoundingClientRect();
  if (sidebarRect && sidebarRect.width > 0) {
    if (sidebarRect.left <= 1) {
      viewportLeftEdge = sidebarRect.right;
    } else if (sidebarRect.right >= viewportWidth - 1) {
      viewportRightEdge = sidebarRect.left;
    }
  }

  const minLeft = viewportLeftEdge + VIEWPORT_MARGIN;
  const maxLeft = Math.max(minLeft, viewportRightEdge - CURSOR_TOOLTIP_WIDTH - VIEWPORT_MARGIN);
  const maxTop = Math.max(VIEWPORT_MARGIN, viewportHeight - CURSOR_TOOLTIP_HEIGHT - VIEWPORT_MARGIN);

  return {
    position: "fixed" as const,
    left: Math.max(minLeft, Math.min(clientX + CURSOR_TOOLTIP_OFFSET, maxLeft)),
    top: Math.min(clientY + CURSOR_TOOLTIP_OFFSET, maxTop),
    zIndex: 2000,
  };
};

function RankedRowTooltip({ clientX, clientY, row }: RankedHoverState) {
  const { t, i18n } = useTranslation();
  const hasChange =
    typeof row.change_percent === "number" && Number.isFinite(row.change_percent);

  return (
    <div
      className="organic-tooltip organic-tooltip--kpi organic-followers-ranked__tooltip"
      style={getCursorTooltipPosition(clientX, clientY)}
    >
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{row.name}</span>
        </div>
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.followers")}</span>
          <strong className="organic-tooltip__meta-value">
            {row.followersFormattedValue ?? formatNumber(row.followers, {}, i18n.language)}
          </strong>
        </div>
        {hasChange ? (
          <div className="organic-tooltip__meta-line">
            <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.vsPreviousPeriod")}</span>
            <strong className="organic-tooltip__meta-value">
              {`${(row.change_percent as number) > 0 ? "+" : ""}${formatNumber(row.change_percent as number, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, i18n.language)}%`}
            </strong>
          </div>
        ) : null}
      </div>
    </div>
  );
}

interface RankedFollowersPanelProps {
  title: string;
  tooltipInfo: ReturnType<typeof getFacebookFollowersDefinitionInfo>;
  topLabel: string;
  rows: FollowersGeoRow[];
  value: number;
  disabled: boolean;
  onChange: (_value: number) => void;
  formulaTitle: string;
  emptyLabel: string;
}

const RankedFollowersPanel: FC<RankedFollowersPanelProps> = ({
  title,
  tooltipInfo,
  topLabel,
  rows,
  value,
  disabled,
  onChange,
  formulaTitle,
  emptyLabel,
}) => {
  const visibleRows = useMemo(
    () => rows.slice(0, value),
    [rows, value],
  );
  const maxFollowers =
    visibleRows.length > 0
      ? Math.max(...visibleRows.map(row => row.followers))
      : 1;
  const secondMaxFollowers = useMemo(() => {
    if (visibleRows.length < 2) return maxFollowers;
    const sorted = [...visibleRows].sort((a, b) => b.followers - a.followers);
    return sorted[1].followers || 1;
  }, [visibleRows, maxFollowers]);
  const hasOutlier = maxFollowers > secondMaxFollowers * 5;
  const [hover, setHover] = useState<RankedHoverState | null>(null);
  const { i18n } = useTranslation();

  return (
    <div className="organic-followers-ranked organic-content-panel">
      <div className="organic-followers-ranked__header">
        <div className="organic-content-panel__title">
          <h3>{title}</h3>
          <DefinitionTooltip
            label={title}
            info={tooltipInfo}
            formulaTitle={formulaTitle}
          />
        </div>
        <label className="organic-followers-ranked__control">
          <span>{topLabel}</span>
          <select
            value={value}
            disabled={disabled}
            onChange={event => onChange(Number(event.target.value) || 1)}
          >
            {Array.from({ length: MAX_TOP }, (_, index) => index + 1).map(option => (
              <option key={option} value={option}>
                {formatNumber(option, {}, i18n.language)}
              </option>
            ))}
          </select>
        </label>
      </div>

      {visibleRows.length > 0 ? (
        <ul className="organic-followers-ranked__list">
          {visibleRows.map((row, index) => {
            let widthPercent: number;
            if (hasOutlier && row.followers === maxFollowers) {
              widthPercent = 100;
            } else if (hasOutlier) {
              widthPercent = secondMaxFollowers > 0
                ? (row.followers / secondMaxFollowers) * 80
                : 0;
            } else {
              widthPercent = maxFollowers > 0
                ? (row.followers / maxFollowers) * 100
                : 0;
            }
            const rowKey = `${row.name}-${row.followers}-${index}`;
            const handleMouseMove = (event: MouseEvent<HTMLLIElement>) => {
              setHover({ clientX: event.clientX, clientY: event.clientY, row });
            };

            return (
              <li
                key={rowKey}
                className="organic-followers-ranked__item"
                onMouseMove={handleMouseMove}
                onMouseLeave={() => setHover(null)}
              >
                <span className="organic-followers-ranked__name">{row.name}</span>
                <span className="organic-followers-ranked__bar-track">
                  <span
                    className="organic-followers-ranked__bar-fill"
                    style={{
                      width: `${Math.max(4, widthPercent)}%`,
                      backgroundColor: GEO_BAR_COLORS[index % GEO_BAR_COLORS.length],
                    }}
                  />
                </span>
                <strong>{row.followersFormattedValue ?? formatNumber(row.followers, {}, i18n.language)}</strong>
              </li>
            );
          })}
        </ul>
      ) : (
        <div className="organic-followers-ranked__empty">{emptyLabel}</div>
      )}
      {hover && <RankedRowTooltip {...hover} />}
    </div>
  );
};

const FacebookFollowersReport: FC<FacebookFollowersReportProps> = ({
  dateFilters,
  topCountriesCount,
  topCitiesCount,
  setTopCountriesCount,
  setTopCitiesCount,
  todayISO,
}) => {
  const { t, i18n } = useTranslation();
  const apiLang = normalizeApiLang(i18n.language);
  const isRTL = isArabicLanguage(i18n.language);
  const chartRtl = rtlChartProps(isRTL);
  const [trendGranularity, setTrendGranularity] =
    useState<FollowersTrendGranularity>("daily");
  const pdfContainerRef = useRef<HTMLDivElement>(null);
  const {
    appliedStartDate,
    appliedEndDate,
    draftStartDate,
    draftEndDate,
    onApplyDates,
  } = dateFilters;
  const {
    kpis,
    countries,
    cities,
    followerTrend,
    loading,
    error,
  } = useFacebookFollowersReport(
    appliedStartDate,
    appliedEndDate,
    apiLang,
    trendGranularity,
  );

  const fbFollowersNarrativePayload = useMemo<Record<string, unknown>>(
    () => ({
      kpis,
      countries,
      cities,
      follower_trend: { daily: followerTrend, monthly: [] },
    }),
    [kpis, countries, cities, followerTrend],
  );

  const { narrative: fbFollowersNarrative, loading: fbFollowersNarrativeLoading, aiSummary } = useDashboardNarrative({
    dashboardKey: "organic_media",
    tabKey: "facebook_followers_report",
    startDate: appliedStartDate,
    endDate: appliedEndDate,
    data: fbFollowersNarrativePayload,
    promptVersion: "v6",
    enabled: !loading && !error && Boolean(kpis.length),
  });

  const countriesSliderMax = MAX_TOP;
  const citiesSliderMax = MAX_TOP;
  const defaultCountriesCount = Math.max(
    1,
    Math.min(countries.length || 1, MAX_TOP)
  );
  const defaultCitiesCount = Math.max(1, Math.min(cities.length || 1, MAX_TOP));
  const countriesSliderValue = Math.min(
    countriesSliderMax,
    Math.max(1, topCountriesCount ?? defaultCountriesCount),
  );
  const citiesSliderValue = Math.min(
    citiesSliderMax,
    Math.max(1, topCitiesCount ?? defaultCitiesCount),
  );

  const trendChartData = useMemo(() => {
    const sortedTrend = [...followerTrend].sort((left, right) => {
      const leftMs = parseTrendDateMs(left.date);
      const rightMs = parseTrendDateMs(right.date);
      if (Number.isNaN(leftMs) || Number.isNaN(rightMs)) {
        return left.date.localeCompare(right.date);
      }
      return leftMs - rightMs;
    });

    if (sortedTrend.length === 0) {
      return [];
    }

    return sortedTrend.map(point => ({
      date: point.date,
      value: Number(point.value ?? 0),
      valueFormattedValue: point.valueFormattedValue,
    }));
  }, [followerTrend]);

  return (
    <div ref={pdfContainerRef}>
      <DashboardSectionHeader
        className="organic-content-report__section-header"
        title={t("organicMedia.tabs.facebookFollowers")}
        subtitle={t("organicMedia.followersReport.facebookPageSubtitle")}
        actionsClassName="dashboard-section-header__actions--filters"
        actions={
          <>
            <div className="dashboard-section-header__control dashboard-section-header__control--wide">
              <OrganicDateRangePicker
                startDate={draftStartDate}
                endDate={draftEndDate}
                maxDate={todayISO}
                onRangeChange={onApplyDates}
              />
            </div>
            <DashboardExportButton
              dashboardName="Facebook Followers"
              dateRange={{ startDate: appliedStartDate, endDate: appliedEndDate }}
              containerRef={pdfContainerRef}
              disabled={loading || Boolean(error)}
            />
          </>
        }
      />

      {error && (
        <div className="organic-tabs-dashboard__error" role="alert">
          {error}
        </div>
      )}

      <div data-pdf-block="narrative">
        <DashboardNarrativePanel
          narrative={fbFollowersNarrative}
          loading={loading || fbFollowersNarrativeLoading}
          aiSummary={aiSummary}
        />
      </div>

      {loading ? (
        <LoadingSpinner compact />
      ) : (
        <div className="organic-facebook-followers-layout">
          <div className="organic-facebook-followers-layout__top" data-pdf-block="followers-overview">
            <div className="organic-facebook-followers-layout__kpis">
              {kpis.slice(0, 2).map((card, index) => {
                const isTotalFollowers = card.title.trim().toLowerCase() === "total followers";
                const comparisonPercent =
                  typeof card.percentageChange === "number" && Number.isFinite(card.percentageChange)
                    ? card.percentageChange
                    : null;
                const hasComparison = !isTotalFollowers && comparisonPercent !== null;
                const comparisonTone =
                  comparisonPercent === null
                    ? "neutral"
                    : comparisonPercent > 0
                      ? "positive"
                      : comparisonPercent < 0
                        ? "negative"
                        : "neutral";
                return (
                  <div
                    key={card.title}
                    className="organic-followers-kpi organic-followers-kpi--facebook-modern"
                  >
                    <div className="organic-followers-kpi__badge" aria-hidden>
                      {index === 0 ? (
                        <SquareUserRound size={18} />
                      ) : (
                        <UserRoundPlus size={18} />
                      )}
                    </div>
                    <div className="organic-content-panel__title">
                      <h3>{card.title}</h3>
                      <DefinitionTooltip
                        label={card.title}
                        info={getFacebookFollowersDefinitionInfo(
                          card.title,
                          (card as { tooltipLabel?: string }).tooltipLabel,
                          t,
                        )}
                        align={index >= 2 ? "end" : "start"}
                        formulaTitle={t("organicMedia.common.formula")}
                      />
                    </div>
                    <div className="organic-followers-kpi__content">
                      <div className="organic-followers-kpi__meta">
                        <div className="organic-followers-kpi__value">
                          {formatKpiCardValue(card.title, card.value, i18n.language)}
                        </div>
                        {!isTotalFollowers && hasComparison && comparisonPercent !== null ? (
                          <div className="organic-followers-kpi__period">
                            <span
                              className={`organic-followers-kpi__change organic-followers-kpi__change--${comparisonTone}`}
                            >
                              {comparisonPercent > 0 ? (
                                <TrendingUp size={12} aria-hidden />
                              ) : comparisonPercent < 0 ? (
                                <TrendingDown size={12} aria-hidden />
                              ) : null}
                              {`${formatNumber(comparisonPercent, { minimumFractionDigits: 2, maximumFractionDigits: 2 }, i18n.language)}%`}
                            </span>{" "}
                            {formatComparisonLabel(card.comparisonLabel, t)}
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="organic-followers-trend organic-followers-trend--modern organic-content-panel">
              <div className="organic-content-panel__header">
                <div className="organic-content-panel__title">
                  <h3>{t("organicMedia.followers.newFollowersTitle")}</h3>
                  <DefinitionTooltip
                    label={t("organicMedia.followers.newFollowersTitle")}
                    info={{
                      description: t("organicMedia.followers.newFollowersDescription"),
                      formula: t("organicMedia.followers.newFollowersFormula"),
                    }}
                    formulaTitle={t("organicMedia.common.formula")}
                  />
                </div>
                <div className="organic-followers-trend__toggle" role="group" aria-label={t("organicMedia.followers.trendGranularityAria")}>
                  {(["daily", "monthly"] as const).map(option => (
                    <button
                      key={option}
                      type="button"
                      className={
                        trendGranularity === option
                          ? "organic-followers-trend__toggle-button organic-followers-trend__toggle-button--active"
                          : "organic-followers-trend__toggle-button"
                      }
                      onClick={() => setTrendGranularity(option)}
                    >
                      {t(`organicMedia.contentReport.${option}`)}
                    </button>
                  ))}
                </div>
              </div>
              {trendChartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={260}>
                  <LineChart
                    data={trendChartData}
                    margin={mirrorChartMargin({ top: 8, right: 14, left: -8, bottom: 0 }, isRTL)}
                  >
                    <CartesianGrid stroke="#e8eef6" strokeDasharray="4 4" vertical={false} />
                    <XAxis
                      dataKey="date"
                      reversed={chartRtl.axisReversed}
                      tick={{ fill: "#8aa0bd", fontSize: 12 }}
                      tickFormatter={value =>
                        i18n.language?.toLowerCase().startsWith("ar")
                          ? toArabicIndicDigitsInString(formatTrendAxisDate(value))
                          : formatTrendAxisDate(value)
                      }
                      axisLine={{ stroke: "#d7dde7" }}
                      tickLine={false}
                      interval="preserveStartEnd"
                      height={30}
                    />
                    <YAxis
                      orientation={chartRtl.yAxisOrientation}
                      tick={{ fill: "#8aa0bd", fontSize: 12 }}
                      tickFormatter={value => formatNumber(value, {}, i18n.language)}
                      axisLine={{ stroke: "#d7dde7" }}
                      tickLine={false}
                      width={44}
                    />
                    <Tooltip
                      content={<FollowersTrendTooltip />}
                      allowEscapeViewBox={{ x: false, y: false }}
                      wrapperStyle={{ pointerEvents: "none" }}
                    />
                    <Line
                      type="monotone"
                      dataKey="value"
                      name={t("organicMedia.followers.newFollowersTitle")}
                      stroke={CHART_PRIMARY_COLOR}
                      strokeWidth={2.5}
                      dot={false}
                      activeDot={{ r: 4 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <div className="organic-followers-trend__empty">
                  {t("webAnalytics.noData")}
                </div>
              )}
            </div>
          </div>

          <div className="organic-facebook-followers-layout__geo" data-pdf-block="followers-geo">
            <RankedFollowersPanel
              title={t("organicMedia.followers.totalByCountry")}
              tooltipInfo={getFacebookFollowersDefinitionInfo("Total Followers by Country", undefined, t)}
              topLabel={t("organicMedia.followers.topCountryLabel")}
              rows={countries}
              value={countriesSliderValue}
              disabled={countries.length === 0}
              onChange={value => {
                const n = Math.max(1, Math.min(countriesSliderMax, value));
                setTopCountriesCount(n);
              }}
              formulaTitle={t("organicMedia.common.formula")}
              emptyLabel={t("webAnalytics.noData")}
            />
            <RankedFollowersPanel
              title={t("organicMedia.followers.totalByCity")}
              tooltipInfo={getFacebookFollowersDefinitionInfo("Total Followers by City", undefined, t)}
              topLabel={t("organicMedia.followers.topCitiesLabel")}
              rows={cities}
              value={citiesSliderValue}
              disabled={cities.length === 0}
              onChange={value => {
                const n = Math.max(1, Math.min(citiesSliderMax, value));
                setTopCitiesCount(n);
              }}
              formulaTitle={t("organicMedia.common.formula")}
              emptyLabel={t("webAnalytics.noData")}
            />
          </div>

        </div>
      )}
    </div>
  );
};

export default FacebookFollowersReport;
